import React, { useState, useCallback, useEffect } from 'react';
import debounce from 'lodash.debounce';
import axios from '../../utils/axiosConfig';
import { setAuthToken } from '../../utils/auth';
import '../../styles/CustomerSearch.css';


function CustomerSearch({ onSelectCustomer }) {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const debouncedSearch = useCallback(
    debounce(async (searchQuery) => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        setError(null);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (token) {
        setAuthToken(token);
      }
      
      try {
        const response = await axios.get(`/api/vendors/search-customers?query=${searchQuery}`);
        console.log('Search response:', response.data);
        setSearchResults(response.data);
        
        if (response.data.length === 0) {
          setError('No customers found matching your search');
        }
      } catch (error) {
        console.error('Error searching customers:', error.response?.data || error.message);
        setError('Error searching for customers');
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setAuthToken(token);
    }
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    if (value.length < 2) {
      setSearchResults([]);
      setError(null);
      return;
    }
    
    debouncedSearch(value);
  };

  const handleClearSearch = () => {
    setQuery('');
    setSearchResults([]);
    setError(null);
  };

  return (
    <div className="cr-search-section">
      <div className="cr-search-container">
        <label htmlFor="customerSearch" className="cr-search-label">Search for a customer</label>
        <div className="cr-search-input-container">
          <div className="cr-search-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
          <input
            id="customerSearch"
            type="text"
            className="cr-search-input"
            value={query}
            onChange={handleInputChange}
            placeholder="Search by name, phone, or loyalty number"
          />
          {query && (
            <button className="cr-search-clear" onClick={handleClearSearch}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
          {isLoading && (
            <div className="cr-search-spinner">
              <svg className="cr-spinner-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 6v6l4 2"></path>
              </svg>
            </div>
          )}
        </div>
      </div>
      
      {error && (
        <div className="cr-search-error">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{error}</span>
        </div>
      )}
      
      {searchResults.length > 0 && (
        <div className="cr-search-results">
          {searchResults.map(customer => (
            <div key={customer.id} className="cr-customer-result" onClick={() => onSelectCustomer(customer)}>
              <div className="cr-customer-avatar">
                {customer.full_name.charAt(0).toUpperCase()}
              </div>
              <div className="cr-customer-info">
                <h3 className="cr-customer-name">{customer.full_name}</h3>
                <div className="cr-customer-details">
                  <span className="cr-customer-phone">{customer.phone || 'No phone'}</span>
                  <span className="cr-customer-divider">•</span>
                  <span className="cr-customer-loyalty">#{customer.loyalty_number || 'No loyalty number'}</span>
                </div>
              </div>
              <button className="cr-view-button">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"></path>
                  <path d="M12 5l7 7-7 7"></path>
                </svg>
                <span>View</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
 }
    
export default CustomerSearch;